import {
  Building2,
  ChevronDown,
  FileText,
  LayoutDashboard,
  LoaderCircle,
  LogOut,
  UserRound,
  Users,
} from "lucide-react";
import React from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../api/client";
import type { TenantInfo } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { TenantLogo } from "../components/TenantLogo";
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui";

export function ProtectedRoute() {
  const auth = useAuth();

  if (auth.status === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-600">
        <LoaderCircle className="mr-2 animate-spin" size={19} />
        Restaurando sua sessão…
      </main>
    );
  }
  if (auth.status === "selecting") {
    return <Navigate to="/select-supplier" replace />;
  }
  if (auth.status === 'consenting') return <main className="min-h-screen bg-slate-50"/>;
  if (auth.status !== "authenticated" || !auth.me) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

export function PortalAdminRoute() {
  const auth = useAuth();
  if (!auth.me || Number(auth.me.role) !== 1) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

export function PortalLayout({ tenant }: { tenant: TenantInfo }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [error, setError] = React.useState("");
  const me = auth.me!;

  async function switchSupplier(value: string) {
    const supplierUserId = Number(value);
    if (supplierUserId === me.supplier.supplierUserId) return;
    setSwitching(true);
    setError("");
    try {
      await auth.switchSupplier(supplierUserId);
      setMenuOpen(false);
    } catch (requestError) {
      setError(
        getApiErrorMessage(requestError, "Não foi possível trocar de empresa.")
      );
    } finally {
      setSwitching(false);
    }
  }

  async function logout() {
    setSwitching(true);
    await auth.logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-5 sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <TenantLogo tenant={tenant} compact />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-950">
                {tenant.nmtenant}
              </p>
              <p className="truncate text-xs text-slate-500">
                Portal do fornecedor
              </p>
            </div>
          </div>

          <nav className="ml-auto hidden items-center gap-1 md:flex">
            <PortalNavLink to="/" end icon={LayoutDashboard}>
              Visão geral
            </PortalNavLink>
            <PortalNavLink to="/documentation" icon={FileText}>
              Documentação
            </PortalNavLink>
            {Number(me.role) === 1 && (
              <PortalNavLink to="/users" icon={Users}>
                Usuários
              </PortalNavLink>
            )}
          </nav>

          <div className="relative">
            <Button
              type="button"
              variant="ghost"
              className="h-auto gap-3 rounded-xl px-2 py-2 text-left hover:bg-slate-100 hover:text-slate-900 focus-visible:border-transparent focus-visible:ring-slate-200"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
            >
              <span className="hidden text-right sm:block">
                <span className="block max-w-48 truncate text-sm font-medium text-slate-900">
                  {me.person.name}
                </span>
                <span className="block max-w-48 truncate text-xs text-slate-500">
                  {me.supplier.name}
                </span>
              </span>
              <span className="flex size-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                <UserRound size={18} />
              </span>
              <ChevronDown size={16} className="text-slate-500" />
            </Button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-xl shadow-slate-900/10"
              >
                <div className="border-b border-slate-100 px-2 pb-3">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {me.person.name}
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    {me.person.email}
                  </p>
                </div>
                <Label className="mt-3 block px-2 text-xs font-medium text-slate-600">
                  Empresa atual
                  <span className="relative mt-1.5 block">
                    <Building2
                      className="pointer-events-none absolute left-3 top-2.5 text-slate-500"
                      size={17}
                    />
                    <Select
                      value={String(me.supplier.supplierUserId)}
                      onValueChange={switchSupplier}
                      disabled={switching || me.suppliers.length < 2}
                    >
                      <SelectTrigger className="h-11 w-full rounded-lg border-slate-300 bg-white pl-10 pr-9 text-slate-700 shadow-none hover:border-slate-400 focus-visible:border-teal-600 focus-visible:ring-teal-600/15 disabled:cursor-default disabled:bg-white disabled:text-slate-700 disabled:opacity-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {me.suppliers.map((supplier) => (
                          <SelectItem
                            value={String(supplier.supplierUserId)}
                            key={supplier.supplierUserId}
                          >
                            {supplier.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {switching && (
                      <LoaderCircle
                        className="absolute right-3 top-2.5 animate-spin text-teal-700"
                        size={17}
                      />
                    )}
                  </span>
                </Label>
                {error && (
                  <p className="mx-2 mt-2 text-xs text-red-600">{error}</p>
                )}
                <Button
                  type="button"
                  role="menuitem"
                  variant="ghost"
                  onClick={logout}
                  disabled={switching}
                  className="-mx-1 mt-3 h-10 w-[calc(100%+0.5rem)] justify-start rounded-lg px-3 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                >
                  <LogOut size={16} />
                  Sair do portal
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <nav className="mb-6 flex flex-wrap gap-2 md:hidden">
          <PortalNavLink to="/" end icon={LayoutDashboard}>
            Visão geral
          </PortalNavLink>
          <PortalNavLink to="/documentation" icon={FileText}>
            Documentação
          </PortalNavLink>
          {Number(me.role) === 1 && (
            <PortalNavLink to="/users" icon={Users}>
              Usuários
            </PortalNavLink>
          )}
        </nav>
        <Outlet />
      </main>
    </div>
  );
}

function PortalNavLink({
  to,
  end,
  icon: Icon,
  children,
}: React.PropsWithChildren<{
  to: string;
  end?: boolean;
  icon: typeof LayoutDashboard;
}>) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
          isActive
            ? "bg-teal-50 text-teal-800"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      <Icon size={17} />
      {children}
    </NavLink>
  );
}
